import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import {
  isInternalStage,
  STAGE_LABELS,
  STAGE_BADGE_CLASS,
  STATUS_LABELS,
  formatExpiryCountdown,
  hasLiveLink,
  isTerminalStatus,
} from '../utils/linkTokenFormat';

// Dot-pill styling per status, matching the app's status-pill language.
const STATUS_PILL_STYLES = {
  ISSUED: { pill: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  VIEWED: { pill: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  CONSUMED: { pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  EXPIRED: { pill: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
  SUPERSEDED: { pill: 'bg-slate-50 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
  REVOKED: { pill: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
};

const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-emerald-100 text-emerald-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '?';
};

const getAvatarColor = (name) => {
  if (!name) return 'bg-slate-100 text-slate-600';
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const formatDate = (value) =>
  new Date(value).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

const formatTime = (value) =>
  new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const StageBadge = ({ stage }) => (
  <Badge variant="outline" className={STAGE_BADGE_CLASS[stage] || 'bg-slate-100 text-slate-800'}>
    {STAGE_LABELS[stage] || stage}
  </Badge>
);

const StatusPill = ({ status }) => {
  const style = STATUS_PILL_STYLES[status] || {
    pill: 'bg-slate-50 text-slate-700 border-slate-200',
    dot: 'bg-slate-400',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${style.pill}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
      {STATUS_LABELS[status] || status}
    </span>
  );
};

const ExpiryCell = ({ expiresAt }) => {
  const c = formatExpiryCountdown(expiresAt);
  const color = c.expired ? 'text-red-600' : c.soon ? 'text-amber-600' : 'text-slate-500';
  return (
    <div>
      <div className="text-sm text-slate-700 whitespace-nowrap">
        {formatDate(expiresAt)}{' '}
        <span className="text-xs text-slate-400">{formatTime(expiresAt)}</span>
      </div>
      <div className={`text-xs font-medium ${color}`}>{c.text}</div>
    </div>
  );
};

const LinkTokenTable = ({
  tokens,
  loading,
  pagination,
  onPageChange,
  onRegenerate,
  onRemind,
  remindingId,
  onExtend,
  onRevoke,
  onCopy,
  onViewTimeline,
}) => {
  if (loading && tokens.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading links...</p>
        </div>
      </div>
    );
  }

  if (tokens.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Link" size={48} className="mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-1">No links found</h3>
        <p className="text-slate-500">Try adjusting your search or filter criteria</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Vendor / Recipient
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Stage
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Status
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Expires
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Regen
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Issued By
              </th>
              <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {tokens.map((row) => {
              const live = hasLiveLink(row);
              const terminal = isTerminalStatus(row.status);
              // CONSUMED means the vendor already submitted through this link, so
              // the source row is marked used and no resend is possible.
              const consumed = row.status === 'CONSUMED';
              // The document-viewer link goes to approvers and is shared between
              // them, so it is never regenerated and the vendor is never nudged
              // about it.
              const internal = isInternalStage(row.stage);
              const displayName = row.vendor_company_name || row.recipient_email || '—';
              return (
                <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                  {/* Vendor / Recipient */}
                  <td className="px-3 py-3.5">
                    <div className="flex items-center min-w-0">
                      <div
                        className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold ${getAvatarColor(displayName)}`}
                      >
                        {getInitials(row.vendor_company_name) !== '?'
                          ? getInitials(row.vendor_company_name)
                          : (row.recipient_email?.[0] || '?').toUpperCase()}
                      </div>
                      <div className="ml-3 min-w-0">
                        {/* Once a registration is submitted the vendor record
                            exists, and the address they typed into the form is
                            often NOT the address we invited. Link through to the
                            vendor so "who did I invite" resolves to "which
                            vendor did that become". */}
                        {row.vendor_company_name ? (
                          row.vendor_id ? (
                            <Link
                              to={`/vendor-profile-details/${row.vendor_id}`}
                              className="text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline truncate max-w-[200px] block"
                              title={`Open ${row.vendor_company_name}`}
                            >
                              {row.vendor_company_name}
                            </Link>
                          ) : (
                            <div
                              className="text-sm font-medium text-slate-900 truncate max-w-[200px]"
                              title={row.vendor_company_name}
                            >
                              {row.vendor_company_name}
                            </div>
                          )
                        ) : (
                          <div className="text-sm text-slate-400" title="Not registered yet">
                            Not registered yet
                          </div>
                        )}
                        <div
                          className="text-xs text-slate-500 truncate max-w-[200px]"
                          title={row.recipient_email}
                        >
                          {row.recipient_email}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Stage */}
                  <td className="px-3 py-3.5 whitespace-nowrap">
                    <StageBadge stage={row.stage} />
                  </td>

                  {/* Status */}
                  <td className="px-3 py-3.5 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <StatusPill status={row.status} />
                      {row.has_active_successor && (
                        <span
                          title="A newer active link supersedes this one"
                          className="text-xs text-slate-400"
                        >
                          <Icon name="ArrowRight" size={14} />
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Expires */}
                  <td className="px-3 py-3.5 whitespace-nowrap">
                    <ExpiryCell expiresAt={row.expires_at} />
                  </td>

                  {/* Regenerations */}
                  <td className="px-3 py-3.5 whitespace-nowrap">
                    <span className="text-sm text-slate-700">{row.regeneration_count}</span>
                  </td>

                  {/* Issued By */}
                  <td className="px-3 py-3.5">
                    <div
                      className="text-sm text-slate-700 truncate max-w-[140px]"
                      title={row.issuer_name || '—'}
                    >
                      {row.issuer_name || '—'}
                    </div>
                    {row.issuer_email && (
                      <div
                        className="text-xs text-slate-500 truncate max-w-[140px]"
                        title={row.issuer_email}
                      >
                        {row.issuer_email}
                      </div>
                    )}
                  </td>

                  {/* Actions — icon buttons in fixed slots so columns align across rows */}
                  <td className="px-3 py-3.5 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-1">
                      {/* Regenerate — primary use case, must work on EXPIRED rows.
                          Blocked once the vendor has actually used the link: the
                          backend refuses ("Cannot resend a used invite"), so an
                          enabled button here would only ever produce an error. */}
                      {internal ? (
                        <span title="Approvers share this document viewer link — regenerating it would cut off whoever is already using it">
                          <Button
                            variant="outline"
                            size="icon"
                            disabled
                            iconName="RefreshCw"
                            aria-label="Regenerate link (shared with approvers)"
                          />
                        </span>
                      ) : consumed ? (
                        <span title="This link was already used — there is nothing to resend">
                          <Button
                            variant="outline"
                            size="icon"
                            disabled
                            iconName="RefreshCw"
                            aria-label="Regenerate link (already used)"
                          />
                        </span>
                      ) : (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => onRegenerate(row)}
                          iconName="RefreshCw"
                          title="Regenerate link"
                          aria-label="Regenerate link"
                          className="text-slate-600 hover:text-blue-600 hover:border-blue-300"
                        />
                      )}

                      {/* Remind — nudge the vendor while the link is still open */}
                      {live && !internal && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => onRemind(row)}
                          loading={remindingId === row.id}
                          disabled={remindingId != null}
                          iconName="BellRing"
                          title="Send reminder to vendor"
                          aria-label="Send reminder to vendor"
                          className="text-slate-600 hover:text-amber-600 hover:border-amber-300"
                        />
                      )}

                      {/* Copy — only where a live link exists */}
                      {live && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => onCopy(row)}
                          iconName="Copy"
                          title="Copy link"
                          aria-label="Copy link"
                          className="text-slate-600 hover:text-slate-900"
                        />
                      )}

                      {/* Extend — meaningful only while a link is active */}
                      {!terminal && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => onExtend(row)}
                          iconName="CalendarClock"
                          title="Extend expiry"
                          aria-label="Extend expiry"
                          className="text-slate-600 hover:text-slate-900"
                        />
                      )}

                      {/* Revoke — hidden for CONSUMED / SUPERSEDED / REVOKED */}
                      {!terminal && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => onRevoke(row)}
                          iconName="Ban"
                          title="Revoke link"
                          aria-label="Revoke link"
                          className="text-slate-400 hover:text-red-600 hover:border-red-300"
                        />
                      )}

                      {/* View timeline */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => onViewTimeline(row)}
                        iconName="History"
                        title="View timeline"
                        aria-label="View timeline"
                        className="text-slate-600 hover:text-slate-900"
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination — server-side, matching the previous implementation */}
      {pagination.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} links
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              iconName="ChevronLeft"
            >
              Previous
            </Button>
            <span className="text-sm text-slate-600 px-2">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              iconName="ChevronRight"
              iconPosition="right"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkTokenTable;
